import { ShieldCheck } from "lucide-react";

import { Link } from "@/components/Link";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import type { TrackableStore } from "@/lib/posthog";
// Relative on purpose: the vitest runner has no "@" alias, and the real props builder must run in
// the render test rather than a stand-in.
import { blogLinkEventProps } from "../../lib/blog/tracking";

/** The trust-check post every store and category page links into (D-13). */
export const TRUST_POST = "how-to-verify-online-store";

// Identical, generic copy on every page regardless of the store's reviews, so the link never
// reads as a verdict about the store it sits on (UI-SPEC section 6, T-51-27).
const COPY = {
  store_page: "إزاي تعرف إن المتجر موثوق قبل ما تدفع؟",
  category_page: "قبل ما تطلب أونلاين: إزاي تتأكد إن المتجر موثوق",
} as const;

type TrustLinkSurface = keyof typeof COPY;

/**
 * The contextual link from store and category pages into the trust-check post. It is the ONE
 * place `blog_link_clicked` is captured for these surfaces (D-16): call sites only pass `surface`
 * and, on the store page, the store. Rendered unconditionally and without browser globals so the
 * link is in the SSR HTML (a crawlable internal link).
 */
export function BlogTrustLink({
  surface,
  store,
}: {
  surface: TrustLinkSurface;
  store?: TrackableStore | null;
}) {
  const { trackBlog } = usePostHogTracking();
  const href = `/blog/${TRUST_POST}`;

  const handleClick = () => {
    const props = blogLinkEventProps({ href, surface, store });
    if (props) trackBlog.linkClicked(props);
  };

  return (
    <Link
      to={href}
      onClick={handleClick}
      className="inline-flex min-h-11 items-center gap-2 rounded text-sm hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange-500"
      style={{ color: "var(--ys-ink-2)" }}
    >
      <ShieldCheck size={16} aria-hidden="true" />
      <span>{COPY[surface]}</span>
    </Link>
  );
}

export default BlogTrustLink;
